export const MARKETING_PATHS = {
  ROOT: '/',
  CONTACT: '/contact',
  HOME: '/home',
} as const;

export const AUTH_PATHS = {
  SIGNIN: '/signin',
  SIGNUP: '/signup',
} as const;

export const ADMIN_PATHS = {
  ROOT: '/',
  DASHBOARD: '/dashboard',
  ORGANIZATIONS: '/organizations',
  ORGANIZATION_DETAIL: (id: string | number) => `/organizations/${id}`,
  USERS: '/users',
  USER_DETAIL: (id: string | number) => `/users/${id}`,
  INTEGRATIONS: '/integrations',
  INTEGRATION_TYPES: '/integration-types',
  ROLE_ACCESS: '/role-access',
  AUDIT_LOG: '/audit-log',
  PLATFORM_SETTINGS: '/platform-settings',
  SOP_VIDEOS: '/sop-videos',
  SOP_VIDEO_DETAIL: (id: string) => `/sop-videos/${id}`,
  AI_USAGE: '/ai-usage',
  MODEL_COMPARE: '/model-compare',
} as const;

export const TENANT_PATHS = {
  SOX_DASHBOARD: '/sox-dashboard',
  SOX_PROCESS: '/sox-cycles/:cycleId/process/:processId',
  SOX_PROCESSES: '/sox-cycles/:cycleId/processes',
  SOX_PROCESS_CREATE: '/sox-cycles/:cycleId/processes/create',
  SOX_PROCESS_EDIT: '/sox-cycles/:cycleId/processes/:processId/edit',
  SOX_PROCESS_UPLOAD: '/sox-cycles/:cycleId/process-upload',
  SOX_CYCLE_END_TO_END: '/sox-cycles/:cycleId/end-to-end-flowchart',
  SOX_CYCLE_COMBINED_FLOWCHART: '/sox-cycles/:cycleId/combined-flowchart',
  COMPLIANCE_REPORT: '/compliance-report',
  MATERIALITY_DASHBOARD: '/materiality-dashboard',
  MATERIALITY: '/materiality',
  RISK_ASSESSMENT: '/risk-assessment',
  SOX_FLOWCHARTS: '/sox-flowcharts',
  SOX_CONTROL_LIBRARY: '/sox-control-library',
  SOX_RISKS: '/sox-risks',
  SOX_RISKS_VIEW: '/sox-risks/:riskId',
  SOX_RISKS_CREATE: '/sox-risks/create',
  SOX_RISKS_EDIT: '/sox-risks/:riskId/edit',
  SOX_CONTROLS: '/sox-controls',
  SOX_SYSTEMS: '/sox-systems',
  SOX_SYSTEMS_NEW: '/sox-systems/new',
  SOX_SYSTEMS_DETAIL: '/sox-systems/:id',
  SOX_REPORTS: '/sox-reports',
  SOX_REPORTS_NEW: '/sox-reports/new',
  SOX_REPORTS_DETAIL: '/sox-reports/:id',
  SOX_CONTROLS_NEW: '/sox-controls/new',
  SOX_CONTROLS_WIZARD: '/sox-controls/create-wizard',
  SOX_CONTROLS_WIZARD_TEST_STEPS: '/sox-controls/create-wizard/test-steps',
  SOX_CONTROLS_WIZARD_DATA_SOURCE: '/sox-controls/create-wizard/data-source',
  SOX_CONTROLS_REFINE: '/sox-controls/refine',
  SOX_CONTROLS_VIEW: '/sox-controls/view/:id',
  SOX_CONTROLS_RUN_TEST: '/sox-controls/:id/run-test',
  SOX_CONTROLS_ATTRIBUTE_RESULT: '/sox-controls/:id/attribute-result',
  SOX_CONTROLS_AI_ENGAGEMENT: '/sox-controls/:id/ai-engagement',
  SOX_MAPPING_REPORT: '/sox-mapping-report',
  VALIDATE: '/validate',
  CONTROL_TESTING_TEST_RUNS: '/control-testing/test-runs',
  SUBSCRIPTIONS: '/subscriptions',
  CREATE_SUBSCRIPTION: '/create-subscription',
  ISSUES_MANAGEMENT: '/issues',
  ISSUE_DETAIL: '/issues/:id',
  REMEDIATION_EXECUTION: '/issues/:id/execution',
  TRANSCRIPTION: '/transcription',
  SOP_SYNTHESIZER: '/sop-synthesizer',
  SOP_SYNTHESIZER_NEW: '/sop-synthesizer/new',
  SOP_SYNTHESIZER_DETAIL: '/sop-synthesizer/:id',
  CONTROLLER_DASHBOARD: '/analytics/controller-dashboard',
  CAE_DASHBOARD: '/analytics/cae-dashboard',
  CFO_DASHBOARD: '/analytics/cfo-dashboard',
  TESTER_DASHBOARD: '/analytics/tester-dashboard',
  AUDITOR_DASHBOARD: '/analytics/auditor-dashboard',
  REVIEWER_DASHBOARD: '/analytics/reviewer-dashboard',
  // Retired 2026-10: now the `pbc_tracker` dashboard widget; redirects to ANALYTICS_DASHBOARD.
  PBC_TRACKER: '/analytics/pbc-tracker',
  HYGIENE_CHECK: '/readiness/hygiene-check',
  // Retired 2026-10: redirect to ANALYTICS_DASHBOARD (kept so old bookmarks resolve).
  MY_DASHBOARD: '/analytics/my-dashboard',
  ANALYTICS_DASHBOARD: '/analytics/dashboard',
  SETTINGS_DASHBOARD_WIDGETS: '/settings/dashboard-widgets',
  // Audit year settings
  SETTINGS_AUDIT_YEARS: '/settings/audit-years',
  SETTINGS_ORG_USERS: '/settings/org-users',
  SETTINGS_ORG_MEMBERS: '/settings/org-members',
  SETTINGS_ORG_AUDIT_LOG: '/settings/org-audit-log',
  // AI provider activity & event tracking
  AI_ACTIVITY: '/ai-activity',
} as const;

export function soxProcessesListPath(cycleId: string): string {
  return `/sox-cycles/${encodeURIComponent(cycleId)}/processes`;
}

export function soxProcessCreatePath(cycleId: string): string {
  return `/sox-cycles/${encodeURIComponent(cycleId)}/processes/create`;
}

export function soxProcessFlowchartPath(cycleId: string, processId: string): string {
  return `/sox-cycles/${encodeURIComponent(cycleId)}/process/${encodeURIComponent(processId)}`;
}

export function soxCycleEndToEndFlowchartPath(cycleId: string): string {
  return `/sox-cycles/${encodeURIComponent(cycleId)}/end-to-end-flowchart`;
}

export function soxCycleCombinedFlowchartPath(cycleId: string): string {
  return `/sox-cycles/${encodeURIComponent(cycleId)}/combined-flowchart`;
}

/** Business `process_id` (e.g. `otc-p3`) in the path for edit / GET /api/processes/{process_id}. */
export function soxProcessEditPath(cycleId: string, processId: string): string {
  return `/sox-cycles/${encodeURIComponent(cycleId)}/processes/${encodeURIComponent(processId)}/edit`;
}

export const DEFAULT_PATHS = {
  ADMIN_LANDING: ADMIN_PATHS.DASHBOARD,
  TENANT_LANDING: TENANT_PATHS.MATERIALITY_DASHBOARD,
} as const;

export const PUBLIC_PATHS: string[] = [
  MARKETING_PATHS.ROOT,
  AUTH_PATHS.SIGNIN,
  AUTH_PATHS.SIGNUP,
  MARKETING_PATHS.CONTACT,
  MARKETING_PATHS.HOME,
] ;

export const APP_SUBDOMAIN_PUBLIC_PATHS: string[] = [
  AUTH_PATHS.SIGNIN,
  AUTH_PATHS.SIGNUP,
] ;

export const ROOT_DOMAIN_PATHS: string[] = [
  MARKETING_PATHS.ROOT,
  MARKETING_PATHS.CONTACT,
  MARKETING_PATHS.HOME,
] ;

export function normalizePath(path: string): string {
  return path.replace(/\/$/, '') || '/';
}

export function isPublicPath(path: string): boolean {
  if (!path || typeof path !== 'string') return false;
  return PUBLIC_PATHS.includes(normalizePath(path));
}
